import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, AlertTriangle, CheckSquare, Square, Wifi, Clock, Activity, Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, XCircle } from "lucide-react";
import { API, PLATFORMS } from "./constants";
import { SyncStatusPanel } from "./SyncStatus";

const SettingsSyncTab = ({ lang = "en", isAdmin = false }) => {
  const [syncStatus, setSyncStatus] = useState({ scheduler_running: false, last_snapshot_at: null, adapter_enabled_accounts: 0 });
  const [adapterCapabilities, setAdapterCapabilities] = useState({});
  const [tikhubStatus, setTikhubStatus] = useState({ status: "unknown", credits_warning: false });
  const [justOneApiStatus, setJustOneApiStatus] = useState({ status: "unknown", balance_warning: false });
  const [syncHealth, setSyncHealth] = useState(null);
  const [syncHealthLoading, setSyncHealthLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncInterval, setSyncInterval] = useState(6);
  const [savingInterval, setSavingInterval] = useState(false);
  const [defaultPeriod, setDefaultPeriod] = useState(7);
  const [savingPeriod, setSavingPeriod] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [syncRes, capRes, tikhubRes, justOneApiRes, intervalRes, periodRes] = await Promise.all([
        axios.get(`${API}/jobs/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/adapters/capabilities`).catch(() => ({ data: {} })),
        axios.get(`${API}/tikhub/status`).catch(() => ({ data: { status: "unknown" } })),
        axios.get(`${API}/justoneapi/status`).catch(() => ({ data: { status: "unknown" } })),
        axios.get(`${API}/settings/sync-interval`).catch(() => ({ data: { interval_hours: 6 } })),
        axios.get(`${API}/settings/default-period`).catch(() => ({ data: { period_days: 7 } })),
      ]);
      setSyncStatus(syncRes.data || {});
      setAdapterCapabilities(capRes.data || {});
      setTikhubStatus(tikhubRes.data || { status: "unknown" });
      setJustOneApiStatus(justOneApiRes.data || { status: "unknown" });
      setSyncInterval(intervalRes.data?.interval_hours || 6);
      setDefaultPeriod(periodRes.data?.period_days || 7);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchSyncHealth = async () => {
    setSyncHealthLoading(true);
    try {
      const res = await axios.get(`${API}/sync-health`);
      setSyncHealth(res.data);
    } catch {
      setSyncHealth(null);
    } finally {
      setSyncHealthLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchSyncHealth();
  }, [isAdmin]);

  const triggerDailySync = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API}/jobs/sync`, {});
      if (response.data?.status === "started") {
        toast.success(lang === "ko" ? "동기화가 백그라운드에서 시작되었습니다" : lang === "zh" ? "同步已在后台启动" : "Sync started in background");
      } else {
        toast.success(`Sync completed: ${response.data.accounts_synced || 0} accounts synced`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger sync");
    } finally {
      setSyncing(false);
    }
  };

  const saveSyncInterval = async (hours) => {
    try {
      setSavingInterval(true);
      await axios.post(`${API}/settings/sync-interval?interval_hours=${hours}`);
      setSyncInterval(hours);
      toast.success(lang === "ko" ? `동기화 주기가 ${hours}시간으로 변경되었습니다` : lang === "zh" ? `同步间隔已更新为 ${hours} 小时` : `Sync interval updated to ${hours} hours`);
    } catch {
      toast.error(lang === "ko" ? "동기화 주기 변경 실패" : lang === "zh" ? "更新同步间隔失败" : "Failed to update sync interval");
    } finally {
      setSavingInterval(false);
    }
  };

  const saveDefaultPeriod = async (days) => {
    try {
      setSavingPeriod(true);
      await axios.post(`${API}/settings/default-period?period_days=${days}`);
      setDefaultPeriod(days);
      localStorage.setItem("defaultPeriodDays", String(days));
      const periodLabels = {
        ko: { 7: "최근 1주일", 14: "최근 2주일", 30: "최근 30일", 60: "최근 60일", 90: "최근 90일" },
        en: { 7: "Last 1 week", 14: "Last 2 weeks", 30: "Last 30 days", 60: "Last 60 days", 90: "Last 90 days" },
      };
      const label = (periodLabels[lang] || periodLabels.en)[days] || `${days}d`;
      toast.success(lang === "ko" ? `기본 기간 필터가 ${label}(으)로 변경되었습니다` : lang === "zh" ? `默认周期已设置为 ${label}` : `Default period set to ${label}`);
    } catch {
      toast.error(lang === "ko" ? "기본 기간 변경 실패" : lang === "zh" ? "更新默认周期失败" : "Failed to update default period");
    } finally {
      setSavingPeriod(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      {/* Sync Status Card */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Sync Status</CardTitle>
          <CardDescription>
            {lang === "ko" ? "자동 데이터 동기화 상태" : lang === "zh" ? "自动数据同步状态" : "Auto data synchronization status"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{lang === "ko" ? "스케줄러 상태" : lang === "zh" ? "调度器状态" : "Scheduler Status"}</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${syncStatus.scheduler_running ? "bg-emerald-500" : "bg-red-500"}`} />
                <span className="font-medium">{syncStatus.scheduler_running ? (lang === "ko" ? "실행 중" : lang === "zh" ? "运行中" : "Running") : (lang === "ko" ? "중지됨" : lang === "zh" ? "已停止" : "Stopped")}</span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{lang === "ko" ? "동기화 주기" : lang === "zh" ? "同步间隔" : "Sync Interval"}</p>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={String(syncInterval)} onValueChange={(v) => saveSyncInterval(Number(v))} disabled={savingInterval}>
                  <SelectTrigger className="h-8 w-full text-sm sm:w-[140px]" data-testid="sync-interval-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{lang === "ko" ? "1시간마다" : lang === "zh" ? "每1小时" : "Every 1 hour"}</SelectItem>
                    <SelectItem value="2">{lang === "ko" ? "2시간마다" : lang === "zh" ? "每2小时" : "Every 2 hours"}</SelectItem>
                    <SelectItem value="3">{lang === "ko" ? "3시간마다" : lang === "zh" ? "每3小时" : "Every 3 hours"}</SelectItem>
                    <SelectItem value="4">{lang === "ko" ? "4시간마다" : lang === "zh" ? "每4小时" : "Every 4 hours"}</SelectItem>
                    <SelectItem value="6">{lang === "ko" ? "6시간마다" : lang === "zh" ? "每6小时" : "Every 6 hours"}</SelectItem>
                    <SelectItem value="8">{lang === "ko" ? "8시간마다" : lang === "zh" ? "每8小时" : "Every 8 hours"}</SelectItem>
                    <SelectItem value="12">{lang === "ko" ? "12시간마다" : lang === "zh" ? "每12小时" : "Every 12 hours"}</SelectItem>
                    <SelectItem value="24">{lang === "ko" ? "24시간마다" : lang === "zh" ? "每24小时" : "Every 24 hours"}</SelectItem>
                  </SelectContent>
                </Select>
                {savingInterval && <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{lang === "ko" ? "마지막 동기화" : lang === "zh" ? "最近同步" : "Last Sync"}</p>
              <p className="font-medium">{syncStatus.last_snapshot_at ? new Date(syncStatus.last_snapshot_at).toLocaleString() : (lang === "ko" ? "없음" : lang === "zh" ? "从未" : "Never")}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{lang === "ko" ? "어댑터 계정 수" : lang === "zh" ? "已启用适配器的账户" : "Adapter-Enabled Accounts"}</p>
              <p className="font-medium">{syncStatus.adapter_enabled_accounts || 0}</p>
            </div>
          </div>
          <Button onClick={triggerDailySync} disabled={syncing} className="mt-4 w-full sm:w-auto" data-testid="trigger-sync-btn">
            {syncing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {syncing ? (lang === "ko" ? "동기화 중..." : lang === "zh" ? "同步中..." : "Syncing...") : (lang === "ko" ? "수동 동기화 실행" : lang === "zh" ? "手动触发同步" : "Trigger Manual Sync")}
          </Button>
        </CardContent>
      </Card>

      {/* Default Period Filter Card */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg font-heading">{lang === "ko" ? "기본 기간 필터" : lang === "zh" ? "默认周期筛选" : "Default Period Filter"}</CardTitle>
          <CardDescription>{lang === "ko" ? "대시보드 및 분석 페이지에서 기본으로 적용되는 날짜 범위" : lang === "zh" ? "应用于仪表板和分析页面的默认日期范围" : "Default date range applied across dashboard and analytics pages"}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <p className="text-sm text-muted-foreground">{lang === "ko" ? "기본 기간" : lang === "zh" ? "默认周期" : "Default Period"}</p>
            <Select value={String(defaultPeriod)} onValueChange={(v) => saveDefaultPeriod(Number(v))} disabled={savingPeriod}>
              <SelectTrigger className="h-8 w-full text-sm sm:w-[180px]" data-testid="default-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">{lang === "ko" ? "최근 1주일" : lang === "zh" ? "最近1周" : "Last 1 week"}</SelectItem>
                <SelectItem value="14">{lang === "ko" ? "최근 2주일" : lang === "zh" ? "最近2周" : "Last 2 weeks"}</SelectItem>
                <SelectItem value="30">{lang === "ko" ? "최근 30일" : lang === "zh" ? "最近30天" : "Last 30 days"}</SelectItem>
                <SelectItem value="60">{lang === "ko" ? "최근 60일" : lang === "zh" ? "最近60天" : "Last 60 days"}</SelectItem>
                <SelectItem value="90">{lang === "ko" ? "최근 90일" : lang === "zh" ? "最近90天" : "Last 90 days"}</SelectItem>
              </SelectContent>
            </Select>
            {savingPeriod && <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />}
          </div>
        </CardContent>
      </Card>

      {/* Real-time Sync Status */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2"><Wifi className="h-5 w-5 text-primary" />Real-time Sync Status</CardTitle>
          <CardDescription>Live WebSocket connection for sync updates</CardDescription>
        </CardHeader>
        <CardContent><SyncStatusPanel lang={lang} /></CardContent>
      </Card>

      {/* API Health Monitor */}
      {isAdmin && (
        <Card className="bg-card border-border/50" data-testid="api-health-monitor">
          <CardHeader className="py-3 px-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  {lang === "ko" ? "API 상태 모니터" : lang === "zh" ? "API 健康监控" : "API Health Monitor"}
                </CardTitle>
                <CardDescription className="text-xs mt-1">
                  {lang === "ko" ? "플랫폼별 API 엔드포인트 상태 및 동기화 경로" : lang === "zh" ? "各平台API端点状态和同步路由" : "Per-platform API endpoint status and sync routes"}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchSyncHealth} className="h-7 text-xs" disabled={syncHealthLoading} data-testid="api-health-refresh-btn">
                <RefreshCw className={`h-3 w-3 mr-1 ${syncHealthLoading ? "animate-spin" : ""}`} />
                {lang === "ko" ? "검사" : lang === "zh" ? "检查" : "Check"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {syncHealthLoading && !syncHealth ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : syncHealth ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  {["xiaohongshu", "douyin", "tiktok", "instagram", "weibo", "youtube", "bilibili"].map((platform) => {
                    const info = syncHealth[platform];
                    if (!info) return null;
                    const statusColor = info.overall === "ok" ? "bg-emerald-500" : info.overall === "degraded" ? "bg-amber-500" : info.overall === "not_configured" ? "bg-zinc-500" : "bg-red-500";
                    const statusLabel = info.overall === "ok" ? (lang === "ko" ? "정상" : lang === "zh" ? "正常" : "OK") : info.overall === "degraded" ? (lang === "ko" ? "저하" : lang === "zh" ? "降级" : "Degraded") : info.overall === "not_configured" ? (lang === "ko" ? "미설정" : lang === "zh" ? "未配置" : "N/A") : (lang === "ko" ? "중단" : lang === "zh" ? "中断" : "Down");
                    return (
                      <div key={platform} className="flex items-center gap-2 p-2 rounded-md bg-muted/30 border border-border/30" data-testid={`platform-status-${platform}`}>
                        <span className={`h-2 w-2 rounded-full ${statusColor} shrink-0`} />
                        <span className="text-xs capitalize truncate">{platform === "xiaohongshu" ? "XHS" : platform}</span>
                        <span className={`ml-auto text-[10px] font-mono ${info.overall === "ok" ? "text-emerald-400" : info.overall === "degraded" ? "text-amber-400" : "text-muted-foreground"}`}>{statusLabel}</span>
                      </div>
                    );
                  })}
                </div>

                {syncHealth.xiaohongshu?.endpoints && (
                  <div className="border border-border/30 rounded-lg overflow-hidden">
                    <div className="flex flex-col gap-2 bg-muted/20 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs font-medium">{lang === "ko" ? "샤오홍수 (XHS) 엔드포인트" : lang === "zh" ? "小红书 (XHS) 端点" : "Xiaohongshu (XHS) Endpoints"}</span>
                      <Badge variant="outline" className={`text-[10px] ${syncHealth.xiaohongshu.overall === "ok" ? "border-emerald-500/40 text-emerald-400" : syncHealth.xiaohongshu.overall === "degraded" ? "border-amber-500/40 text-amber-400" : "border-red-500/40 text-red-400"}`}>
                        {lang === "ko" ? "활성 경로: " : lang === "zh" ? "路由: " : "Route: "}{syncHealth.xiaohongshu.active_route}
                      </Badge>
                    </div>
                    <div className="divide-y divide-border/20">
                      {syncHealth.xiaohongshu.endpoints.map((ep, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                          {ep.status === "ok" ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" /> : ep.status === "timeout" ? <Clock className="h-3 w-3 text-amber-500 shrink-0" /> : <XCircle className="h-3 w-3 text-red-500 shrink-0" />}
                          <span className="text-muted-foreground truncate">{ep.endpoint}</span>
                          <span className={`ml-auto font-mono text-[10px] ${ep.status === "ok" ? "text-emerald-400" : ep.status === "timeout" ? "text-amber-400" : "text-red-400"}`}>
                            {ep.status === "ok" ? "OK" : ep.status === "timeout" ? "Timeout" : `${ep.http_status || "Err"}`}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        {syncHealth.xiaohongshu.crawler?.available && syncHealth.xiaohongshu.crawler?.cookies_configured ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" /> : syncHealth.xiaohongshu.crawler?.available ? <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" /> : <XCircle className="h-3 w-3 text-red-500 shrink-0" />}
                        <span className="text-muted-foreground">Playwright Crawler</span>
                        <span className={`ml-auto font-mono text-[10px] ${syncHealth.xiaohongshu.crawler?.available && syncHealth.xiaohongshu.crawler?.cookies_configured ? "text-emerald-400" : syncHealth.xiaohongshu.crawler?.available ? "text-amber-400" : "text-red-400"}`}>
                          {syncHealth.xiaohongshu.crawler?.available ? (syncHealth.xiaohongshu.crawler?.cookies_configured ? "OK" : (lang === "ko" ? "쿠키 필요" : lang === "zh" ? "需要Cookie" : "No Cookies")) : (lang === "ko" ? "미설치" : lang === "zh" ? "未安装" : "N/A")}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {syncHealth.douyin?.endpoints && (
                  <div className="border border-border/30 rounded-lg overflow-hidden">
                    <div className="flex flex-col gap-2 bg-muted/20 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs font-medium">{lang === "ko" ? "도우인 (Douyin) 엔드포인트" : lang === "zh" ? "抖音 (Douyin) 端点" : "Douyin Endpoints"}</span>
                      <Badge variant="outline" className={`text-[10px] ${syncHealth.douyin.overall === "ok" ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>
                        {lang === "ko" ? "활성 경로: " : lang === "zh" ? "路由: " : "Route: "}{syncHealth.douyin.active_route}
                      </Badge>
                    </div>
                    <div className="divide-y divide-border/20">
                      {syncHealth.douyin.endpoints.map((ep, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                          {ep.status === "ok" ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" /> : ep.status === "timeout" ? <Clock className="h-3 w-3 text-amber-500 shrink-0" /> : <XCircle className="h-3 w-3 text-red-500 shrink-0" />}
                          <span className="text-muted-foreground truncate">{ep.endpoint}</span>
                          <span className={`ml-auto font-mono text-[10px] ${ep.status === "ok" ? "text-emerald-400" : ep.status === "timeout" ? "text-amber-400" : "text-red-400"}`}>
                            {ep.status === "ok" ? "OK" : ep.status === "timeout" ? "Timeout" : `${ep.http_status || "Err"}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(tikhubStatus.credits_warning || justOneApiStatus.balance_warning || tikhubStatus.last_error || justOneApiStatus.last_error) && (
                  <div className="space-y-2">
                    {tikhubStatus.credits_warning && (
                      <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-500">
                        <strong>TikHub:</strong> {lang === "ko" ? "크레딧 잔액이 부족합니다. " : lang === "zh" ? "积分余额不足。" : "Credits running low. "}<a href="https://tikhub.io/" target="_blank" rel="noopener noreferrer" className="underline">tikhub.io</a>
                      </div>
                    )}
                    {justOneApiStatus.balance_warning && (
                      <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-500">
                        <strong>JustOneAPI:</strong> {lang === "ko" ? "잔액이 부족합니다. " : lang === "zh" ? "余额不足。" : "Balance running low. "}<a href="https://user.justoneapi.com/" target="_blank" rel="noopener noreferrer" className="underline">user.justoneapi.com</a>
                      </div>
                    )}
                    {tikhubStatus.last_error && <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400"><strong>TikHub Error:</strong> {tikhubStatus.last_error}</div>}
                    {justOneApiStatus.last_error && <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400"><strong>JustOneAPI Error:</strong> {justOneApiStatus.last_error}</div>}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground/50 text-right">{lang === "ko" ? "마지막 검사: " : lang === "zh" ? "最近检查: " : "Last check: "}{syncHealth.checked_at ? new Date(syncHealth.checked_at).toLocaleString() : "-"}</p>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground mb-3">{lang === "ko" ? "API 상태를 확인하려면 검사 버튼을 누르세요" : lang === "zh" ? "点击检查按钮测试API端点" : "Click Check to test API endpoints"}</p>
                <Button variant="outline" size="sm" onClick={fetchSyncHealth} data-testid="api-health-initial-check-btn">
                  <Activity className="h-3.5 w-3.5 mr-1.5" />{lang === "ko" ? "상태 검사 시작" : lang === "zh" ? "开始健康检查" : "Run Health Check"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* TikHub API Status */}
      <Card className={`bg-card border-border/50 ${tikhubStatus.credits_warning ? "border-amber-500" : ""}`} data-testid="tikhub-status-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">TikHub API Status{tikhubStatus.credits_warning && <AlertTriangle className="h-4 w-4 text-amber-500" />}</CardTitle>
          <CardDescription>Douyin & Xiaohongshu data integration via TikHub</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tikhubStatus.status === "healthy" ? "bg-emerald-500" : tikhubStatus.status === "credits_low" ? "bg-amber-500" : tikhubStatus.status === "not_configured" ? "bg-gray-500" : "bg-red-500"} text-white`}>
                {tikhubStatus.status === "healthy" ? "Healthy" : tikhubStatus.status === "credits_low" ? "Credits Low" : tikhubStatus.status === "not_configured" ? "Not Configured" : tikhubStatus.status}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2"><span className="text-sm text-muted-foreground">API Key:</span><span className={`text-sm ${tikhubStatus.api_key_configured ? "text-emerald-500" : "text-red-500"}`}>{tikhubStatus.api_key_configured ? "Configured" : "Not Set"}</span></div>
            <div className="flex flex-wrap items-center gap-2"><span className="text-sm text-muted-foreground">Last Check:</span><span className="text-sm">{tikhubStatus.timestamp ? new Date(tikhubStatus.timestamp).toLocaleString() : "Never"}</span></div>
          </div>
          {tikhubStatus.credits_warning && <div className="mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30"><p className="text-sm text-amber-600"><strong>Warning:</strong> TikHub API credits are running low. Please recharge at <a href="https://tikhub.io/" target="_blank" rel="noopener noreferrer" className="underline">tikhub.io</a></p></div>}
          {tikhubStatus.last_error && <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30"><p className="text-sm text-red-600"><strong>Last Error:</strong> {tikhubStatus.last_error}</p></div>}
        </CardContent>
      </Card>

      {/* Just One API Status */}
      <Card className={`bg-card border-border/50 ${justOneApiStatus.balance_warning ? "border-amber-500" : ""}`} data-testid="justoneapi-status-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">Just One API Status{justOneApiStatus.balance_warning && <AlertTriangle className="h-4 w-4 text-amber-500" />}</CardTitle>
          <CardDescription>Weibo & Bilibili data integration via Just One API</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${justOneApiStatus.status === "healthy" ? "bg-emerald-500" : justOneApiStatus.status === "insufficient_balance" ? "bg-amber-500" : justOneApiStatus.status === "not_configured" ? "bg-gray-500" : "bg-red-500"} text-white`}>
                {justOneApiStatus.status === "healthy" ? "Healthy" : justOneApiStatus.status === "insufficient_balance" ? "Balance Low" : justOneApiStatus.status === "not_configured" ? "Not Configured" : justOneApiStatus.status}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2"><span className="text-sm text-muted-foreground">API Token:</span><span className={`text-sm ${justOneApiStatus.api_key_configured ? "text-emerald-500" : "text-red-500"}`}>{justOneApiStatus.api_key_configured ? "Configured" : "Not Set"}</span></div>
            <div className="flex flex-wrap items-center gap-2"><span className="text-sm text-muted-foreground">Last Check:</span><span className="text-sm">{justOneApiStatus.timestamp ? new Date(justOneApiStatus.timestamp).toLocaleString() : "Never"}</span></div>
          </div>
          {justOneApiStatus.balance_warning && <div className="mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30"><p className="text-sm text-amber-600"><strong>Warning:</strong> Just One API balance is running low. Please recharge at <a href="https://user.justoneapi.com/" target="_blank" rel="noopener noreferrer" className="underline">user.justoneapi.com</a></p></div>}
          {justOneApiStatus.last_error && <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30"><p className="text-sm text-red-600"><strong>Last Error:</strong> {justOneApiStatus.last_error}</p></div>}
          <div className="mt-4 p-3 rounded-lg bg-muted/30"><p className="text-xs text-muted-foreground"><strong>Supported Platforms:</strong> Weibo (User Profile + Posts), Bilibili (User Profile + Videos)</p></div>
        </CardContent>
      </Card>

      {/* Data Enrichment */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2"><RefreshCw className="h-5 w-5 text-primary" />Data Enrichment</CardTitle>
          <CardDescription>Enrich existing posts with detailed engagement data (likes, comments, shares)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Use Just One API and platform public APIs to fill in missing engagement metrics for posts that were synced without complete data.</p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" size="sm" onClick={async () => { try { toast.info("Enriching Bilibili posts..."); const res = await axios.post(`${API}/enrich/bilibili`); toast.success(`Enriched ${res.data.enriched} Bilibili posts`); } catch (e) { toast.error(e.response?.data?.detail || "Failed to enrich Bilibili"); } }} className="w-full sm:w-auto" data-testid="enrich-bilibili-btn"><RefreshCw className="h-4 w-4 mr-2" />Enrich Bilibili</Button>
              <Button variant="outline" size="sm" onClick={async () => { try { toast.info("Enriching Xiaohongshu posts..."); const res = await axios.post(`${API}/enrich/xiaohongshu`); toast.success(`Enriched ${res.data.enriched} Xiaohongshu posts`); } catch (e) { toast.error(e.response?.data?.detail || "Failed to enrich Xiaohongshu"); } }} className="w-full sm:w-auto" data-testid="enrich-xhs-btn"><RefreshCw className="h-4 w-4 mr-2" />Enrich Xiaohongshu</Button>
              <Button variant="default" size="sm" onClick={async () => { try { toast.info("Enriching all platforms..."); const res = await axios.post(`${API}/enrich/all`); toast.success(`Enriched ${res.data.total_enriched} posts across all platforms`); } catch (e) { toast.error(e.response?.data?.detail || "Failed to enrich"); } }} className="w-full sm:w-auto" data-testid="enrich-all-btn"><RefreshCw className="h-4 w-4 mr-2" />Enrich All Platforms</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Adapter Capabilities */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Platform Adapter Capabilities</CardTitle>
          <CardDescription>Available features for each platform adapter</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Object.entries(adapterCapabilities).map(([platform, caps]) => (
              <div key={platform} className="p-4 rounded-lg bg-muted/30 border border-border/50">
                <h4 className="font-medium capitalize mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PLATFORMS.find(p => p.id === platform)?.color || "#888" }} />
                  {platform}
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex flex-wrap items-center gap-2">{caps.can_fetch_followers ? <CheckSquare className="h-3 w-3 text-emerald-500" /> : <Square className="h-3 w-3 text-muted-foreground" />}<span className={caps.can_fetch_followers ? "" : "text-muted-foreground"}>Followers</span></div>
                  <div className="flex flex-wrap items-center gap-2">{caps.can_fetch_posts ? <CheckSquare className="h-3 w-3 text-emerald-500" /> : <Square className="h-3 w-3 text-muted-foreground" />}<span className={caps.can_fetch_posts ? "" : "text-muted-foreground"}>Posts</span></div>
                  <div className="flex flex-wrap items-center gap-2">{caps.can_fetch_comments ? <CheckSquare className="h-3 w-3 text-emerald-500" /> : <Square className="h-3 w-3 text-muted-foreground" />}<span className={caps.can_fetch_comments ? "" : "text-muted-foreground"}>Comments</span></div>
                </div>
                {caps.unavailable_reasons && Object.keys(caps.unavailable_reasons).length > 0 && <p className="text-xs text-muted-foreground mt-2 italic">{Object.values(caps.unavailable_reasons)[0]}</p>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsSyncTab;
